import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
export const FILE_URL = (path) => `${API}/files/${path}`;
// Logo: champagne (#B8A88A) on transparent background — hosted in our storage
export const LOGO_URL = `${API}/files/prize-joias/images/85996c9c079544229095b3789095ec26.png`;

export const api = axios.create({ baseURL: API, withCredentials: true });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("prize_admin_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Global 401 handler for admin endpoints: clear token + redirect to login.
let _sessionExpiredNotified = false;
api.interceptors.response.use(
  (resp) => resp,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || "";
    const isAdminCall =
      status === 401 &&
      localStorage.getItem("prize_admin_token") &&
      !url.includes("/auth/") &&
      !url.includes("/wishlist");
    if (isAdminCall) {
      localStorage.removeItem("prize_admin_token");
      if (!_sessionExpiredNotified) {
        _sessionExpiredNotified = true;
        toast.error("Sessão expirada — faça login novamente.");
        setTimeout(() => {
          _sessionExpiredNotified = false;
          if (!window.location.pathname.match(/^\/admin\/?$/)) {
            window.location.href = "/admin";
          }
        }, 800);
      }
    }
    return Promise.reject(error);
  }
);

export const formatBRL = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v || 0);

export const CATEGORIES = [
  { slug: "Colares", label: "Colares" },
  { slug: "Anéis", label: "Anéis" },
  { slug: "Brincos", label: "Brincos" },
  { slug: "Pingentes", label: "Pingentes" },
  { slug: "Pulseiras", label: "Pulseiras" },
  { slug: "Outros", label: "Outros" },
];

// Brand values MUST match exactly what's stored in the DB — bulk-imported
// products use "Tiffany" (not "Tiffany & Co.") and "H Stern" (no dot).
// "Outra" stays here for the admin form's custom-brand workflow, but is
// hidden from the public catalog brand filter below.
export const BRANDS = ["Cartier", "Tiffany", "Bulgari", "Vivara", "H Stern", "Outra"];

// Filter dropdown on the catalog excludes "Outra" — there's no reason for
// a shopper to filter by "no-brand pieces" (which are 955 of the catalogue).
export const CATALOG_BRAND_FILTER_OPTIONS = BRANDS.filter((b) => b !== "Outra");

/**
 * Returns the brand label to display on cards / PDPs.
 *
 * Rules (M214c):
 *   - When brand is "Outra"/"Outras" + custom_brand is filled → use custom_brand
 *   - When brand is "Outra"/"Outras" + no custom_brand → return "" (hide the label)
 *   - When brand is empty/missing → return "" (hide the label)
 *   - Otherwise → return brand as-is
 *
 * Callers should check the result with a truthy guard (`{label && <p>...}`)
 * so the entire <p> element is omitted when no brand is available.
 */
export function displayBrand(product) {
  if (!product) return "";
  const brand = (product.brand || "").trim();
  const custom = (product.custom_brand || "").trim();
  if (brand === "Outra" || brand === "Outras") return custom; // empty if no custom
  return brand; // may be empty — caller should guard
}

export const METALS = ["Ouro amarelo", "Ouro branco", "Ouro rosé", "Prata 925", "Platina", "Prata dourada"];
export const KARATAGES = ["8k", "9k", "10k", "14k", "18k", "24k", "Prata 925", "Prata 950", "Platina 950", "Não se aplica"];
export const STONES = ["Diamante", "Esmeralda", "Rubi", "Safira", "Pérola", "Ametista", "Topázio", "Água-marinha", "Sem pedra", "Outra"];
export const STYLES = ["Clássico", "Moderno", "Minimalista", "Romântico", "Ousado", "Boho", "Vintage", "Geométrico"];
export const OCCASIONS = ["Dia a Dia", "Casamento", "Noivado", "Ocasião Especial"];
export const FOR_WHOM = ["Para ela", "Para ele", "Unissex"];
export const COMBINES = ["Look casual", "Look executivo", "Vestido de festa", "Noiva", "Praia/verão", "All black"];
